import {
    ArrowRight,
    Brain,
    Eye,
    FileCheck,
    Lock,
    Map,
    Shield,
    Zap
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { useAuth } from '../utils/AuthContext'

function Home() {
  const { isAuthenticated } = useAuth()

  const features = [
    {
      icon: <FileCheck className="h-6 w-6" />,
      title: "AI Document Verification",
      description: "Advanced OCR and machine learning algorithms verify document authenticity and extract key information with 99.7% accuracy."
    },
    {
      icon: <Eye className="h-6 w-6" />,
      title: "Biometric Authentication",
      description: "Facial recognition and liveness detection prevent spoofing attacks and ensure real-time identity verification."
    },
    {
      icon: <Brain className="h-6 w-6" />,
      title: "Forgery Detection",
      description: "Sophisticated AI models detect document manipulation, deepfakes, and other fraudulent attempts in real-time."
    },
    {
      icon: <Map className="h-6 w-6" />,
      title: "Geo-Mapping Analytics",
      description: "Visual intelligence dashboard showing geographic patterns of suspicious activities and fraud hotspots."
    },
    {
      icon: <Lock className="h-6 w-6" />,
      title: "Enterprise Security",
      description: "Bank-grade encryption, audit trails, and compliance with global privacy regulations like GDPR and CCPA."
    },
    {
      icon: <Zap className="h-6 w-6" />,
      title: "Real-time Processing",
      description: "Lightning-fast verification results in under 3 seconds with instant notifications and status updates."
    }
  ]

  const stats = [
    { number: "99.7%", label: "Accuracy Rate" },
    { number: "2.8s", label: "Average Processing Time" },
    { number: "10M+", label: "Documents Verified" },
    { number: "150+", label: "Countries Supported" }
  ]

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative bg-black overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="matrix-bg"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="text-center">
            <div className="flex justify-center mb-8">
              <div className="flex items-center space-x-2 bg-black/90 backdrop-blur-md border border-green-500/30 rounded-full px-4 py-2 shadow-lg">
                <Shield className="h-5 w-5 text-green-400" />
                <span className="text-sm font-mono text-gray-300">Trusted by 1000+ Organizations</span>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-green-400 font-mono mb-6">
              <span className="text-blue-400">&lt;</span>AI-Powered{' '}
              <span className="text-gradient-cyber">Digital Identity</span>{' '}
              Verification<span className="text-blue-400">/&gt;</span>
            </h1>
            
            <p className="text-xl text-gray-300 font-mono mb-8 max-w-3xl mx-auto">
              <span className="text-green-500">#</span> Secure, intelligent, and lightning-fast identity verification with advanced 
              AI fraud detection, biometric authentication, and comprehensive geo-mapping analytics.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {isAuthenticated ? (
                <>
                  <Link to="/dashboard" className="bg-green-600/20 hover:bg-green-600/40 text-green-400 hover:text-green-300 px-8 py-3 rounded-md font-mono transition-all duration-300 border border-green-600/30 hover:border-green-500/50 flex items-center justify-center">
                    <span className="text-green-500">[</span>Go to Dashboard<span className="text-green-500">]</span>
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                  <Link to="/verify" className="bg-blue-600/20 hover:bg-blue-600/40 text-blue-400 hover:text-blue-300 px-8 py-3 rounded-md font-mono transition-all duration-300 border border-blue-600/30 hover:border-blue-500/50 flex items-center justify-center">
                    <span className="text-blue-500">{'{'}</span>Verify Document<span className="text-blue-500">{'}'}</span>
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/register" className="bg-green-600/20 hover:bg-green-600/40 text-green-400 hover:text-green-300 px-8 py-3 rounded-md font-mono transition-all duration-300 border border-green-600/30 hover:border-green-500/50 flex items-center justify-center">
                    <span className="text-green-500">[</span>Get Started Free<span className="text-green-500">]</span>
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                  <Link to="/login" className="bg-blue-600/20 hover:bg-blue-600/40 text-blue-400 hover:text-blue-300 px-8 py-3 rounded-md font-mono transition-all duration-300 border border-blue-600/30 hover:border-blue-500/50 flex items-center justify-center">
                    Sign In
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-900 border-t border-green-500/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-bold font-mono text-green-400 mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-400 font-mono text-sm">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-black border-t border-green-500/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold font-mono text-green-400 mb-4">
              <span className="text-green-500">&gt;</span> Advanced Identity Verification Features
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto font-mono">
              <span className="text-blue-500">//</span> Comprehensive suite of AI-powered tools designed to detect fraud, 
              verify authenticity, and provide actionable security insights.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const isGeoMapping = feature.title === "Geo-Mapping Analytics"
              const FeatureCard = (
                <div className="bg-gray-900 border border-green-500/30 p-6 rounded-lg hover:border-green-500/50 hover:bg-gray-800/50 transition-all duration-300">
                  <div className="flex items-center mb-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-green-600/20 border border-green-500/30 rounded-lg flex items-center justify-center text-green-400">
                      {feature.icon}
                    </div>
                    <h3 className="ml-4 text-xl font-mono text-green-400">
                      {feature.title}
                    </h3>
                  </div>
                  <p className="text-gray-300 leading-relaxed font-mono text-sm">
                    {feature.description}
                  </p>
                  {isGeoMapping && isAuthenticated && (
                    <div className="mt-4">
                      <span className="text-blue-400 hover:text-blue-300 font-mono text-sm flex items-center">
                        View Map <ArrowRight className="ml-2 h-4 w-4" />
                      </span>
                    </div>
                  )}
                </div>
              )
              
              return isGeoMapping && isAuthenticated ? (
                <Link key={index} to="/map">
                  {FeatureCard}
                </Link>
              ) : (
                <div key={index}>
                  {FeatureCard}
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-gray-900 border-t border-blue-500/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold font-mono text-blue-400 mb-4">
              <span className="text-blue-500">$</span> How CyberSafe Works
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto font-mono">
              <span className="text-green-500">#</span> Simple, secure, and intelligent verification process in just three steps.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-600/20 border border-green-500/50 rounded-full flex items-center justify-center text-green-400 text-2xl font-bold font-mono mx-auto mb-4">
                1
              </div>
              <h3 className="text-xl font-mono text-green-400 mb-4">Upload Document</h3>
              <p className="text-gray-300 font-mono text-sm">
                Securely upload your identity document using our encrypted upload system. 
                We support IDs, passports, licenses, and more.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600/20 border border-blue-500/50 rounded-full flex items-center justify-center text-blue-400 text-2xl font-bold font-mono mx-auto mb-4">
                2
              </div>
              <h3 className="text-xl font-mono text-blue-400 mb-4">AI Analysis</h3>
              <p className="text-gray-300 font-mono text-sm">
                Our advanced AI analyzes your document for authenticity, extracts key data, 
                and performs biometric verification if required.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600/20 border border-purple-500/50 rounded-full flex items-center justify-center text-purple-400 text-2xl font-bold font-mono mx-auto mb-4">
                3
              </div>
              <h3 className="text-xl font-mono text-purple-400 mb-4">Instant Results</h3>
              <p className="text-gray-300 font-mono text-sm">
                Receive comprehensive verification results with confidence scores, 
                fraud indicators, and actionable security insights.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-green-900/50 to-blue-900/50 border-t border-green-500/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-mono text-green-400 mb-4">
            <span className="text-green-500">{'>'}</span> Ready to Secure Your Digital Identity?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto font-mono">
            <span className="text-blue-500">//</span> Join thousands of organizations worldwide who trust CyberSafe for 
            their identity verification and fraud prevention needs.
          </p>
          
          {!isAuthenticated && (
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register" className="bg-green-600/20 hover:bg-green-600/40 text-green-400 hover:text-green-300 px-8 py-3 rounded-md font-mono transition-all duration-300 border border-green-600/30 hover:border-green-500/50 flex items-center justify-center text-lg">
                <span className="text-green-500">[</span>Start Free Trial<span className="text-green-500">]</span>
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link to="/login" className="bg-blue-600/20 hover:bg-blue-600/40 text-blue-400 hover:text-blue-300 px-8 py-3 rounded-md font-mono transition-all duration-300 border border-blue-600/30 hover:border-blue-500/50 flex items-center justify-center text-lg">
                Sign In
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

export default Home